import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  ["Need to sell soon", "I need to sell soon"],
  ["Moving", "I'm moving"],
  ["Inherited property", "I inherited the property"],
  ["Property needs repairs", "The property needs repairs"],
  ["Just exploring", "I'm just exploring my options"],
];

const inputCls =
  "w-full bg-cream-soft border border-ink/15 px-4 py-3.5 text-ink text-base placeholder:text-ink-soft/60 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300 rounded-sm";
const labelCls = "block font-mono text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-3 mt-6 first:mt-0";

export const LeadForm = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    property_address: "", owner: "", reason: "", timeline: "",
    condition: "", bedrooms: "", bathrooms: "", price: "",
    name: "", phone: "", email: "", contact_method: "Phone call",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validate = () => {
    if (step === 1) {
      if (!data.property_address.trim()) return "Please enter the property address.";
      if (!data.owner) return "Please tell us if you're the owner.";
    }
    if (step === 2) {
      if (!data.reason) return "Please select a reason.";
      if (!data.timeline) return "Please select a timeline.";
    }
    if (step === 3) {
      if (!data.condition) return "Please select the property condition.";
    }
    if (step === 4) {
      if (!data.name.trim()) return "Please enter your name.";
      if (!data.phone.trim()) return "Please enter your phone number.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, data);
      setDone(true);
      toast.success("Your information has been received.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepAnim = {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -24 },
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  };

  const Choice = ({ name, value, label, checked }) => (
    <button
      type="button"
      onClick={() => set(name, value)}
      data-testid={`choice-${name}-${value.replace(/\s+/g, "-").toLowerCase()}`}
      className={`text-left w-full px-4 py-3.5 border text-[15px] rounded-sm transition-colors duration-200 ${
        checked ? "border-gold bg-gold/10 text-ink font-medium" : "border-ink/15 text-ink-soft hover:border-gold/60 hover:bg-gold/5"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="bg-white border border-ink/10 p-7 md:p-10 shadow-[0_30px_80px_rgba(34,48,42,0.14)] rounded-lg"
      data-testid="lead-form-card"
    >
      <div className="h-[3px] bg-ink/10 mb-8 overflow-hidden rounded-full">
        <motion.div
          className="h-full bg-gold"
          animate={{ width: `${(step / 4) * 100}%` }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {done ? (
        <div className="text-center py-10" data-testid="lead-success">
          <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold flex items-center justify-center mx-auto mb-6">
            <Check className="text-gold" size={26} />
          </div>
          <h3 className="font-serif text-3xl text-ink">Thank you.</h3>
          <p className="text-ink-soft mt-3 max-w-sm mx-auto">
            We've received your details and will reach out shortly about your selling options.
          </p>
        </div>
      ) : (
        <>
          <h2 className="font-serif text-3xl text-ink">Tell us about your property</h2>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold mb-8 mt-1">
            Step {step} of 4
          </p>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" {...stepAnim}>
                <label className={labelCls}>Property address</label>
                <input className={inputCls} value={data.property_address}
                  onChange={(e) => set("property_address", e.target.value)}
                  placeholder="123 Main Street, Columbus, GA" data-testid="input-address" />
                <label className={labelCls}>Are you the owner?</label>
                <div className="grid grid-cols-2 gap-3">
                  <Choice name="owner" value="Yes" label="Yes" checked={data.owner === "Yes"} />
                  <Choice name="owner" value="No" label="No" checked={data.owner === "No"} />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" {...stepAnim}>
                <label className={labelCls}>Why are you considering selling?</label>
                <div className="grid gap-2.5">
                  {REASONS.map(([val, lbl]) => (
                    <Choice key={val} name="reason" value={val} label={lbl} checked={data.reason === val} />
                  ))}
                </div>
                <label className={labelCls}>How soon would you consider selling?</label>
                <select className={inputCls} value={data.timeline}
                  onChange={(e) => set("timeline", e.target.value)} data-testid="select-timeline">
                  <option value="">Select one</option>
                  <option>ASAP</option>
                  <option value="Within 1-3 months">Within 1–3 months</option>
                  <option value="Within 3-6 months">Within 3–6 months</option>
                  <option value="6+ months">6+ months</option>
                  <option value="Not sure">Not sure</option>
                </select>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" {...stepAnim}>
                <label className={labelCls}>Property condition</label>
                <select className={inputCls} value={data.condition}
                  onChange={(e) => set("condition", e.target.value)} data-testid="select-condition">
                  <option value="">Select one</option>
                  <option>Move-in ready</option>
                  <option>Needs minor repairs</option>
                  <option>Needs major repairs</option>
                  <option>Major renovation needed</option>
                  <option>Not sure</option>
                </select>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Bedrooms</label>
                    <input type="number" min="0" className={inputCls} value={data.bedrooms}
                      onChange={(e) => set("bedrooms", e.target.value)} placeholder="3" data-testid="input-bedrooms" />
                  </div>
                  <div>
                    <label className={labelCls}>Bathrooms</label>
                    <input type="number" min="0" step="0.5" className={inputCls} value={data.bathrooms}
                      onChange={(e) => set("bathrooms", e.target.value)} placeholder="2" data-testid="input-bathrooms" />
                  </div>
                </div>
                <label className={labelCls}>Ideal price</label>
                <input className={inputCls} value={data.price}
                  onChange={(e) => set("price", e.target.value)} placeholder="$250,000" data-testid="input-price" />
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" {...stepAnim}>
                <label className={labelCls}>Your name</label>
                <input className={inputCls} value={data.name}
                  onChange={(e) => set("name", e.target.value)} placeholder="John Smith" data-testid="input-name" />
                <label className={labelCls}>Phone number</label>
                <input type="tel" className={inputCls} value={data.phone}
                  onChange={(e) => set("phone", e.target.value)} placeholder="(706) 555-1234" data-testid="input-phone" />
                <label className={labelCls}>Email</label>
                <input type="email" className={inputCls} value={data.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="input-email" />
                <label className={labelCls}>Preferred contact method</label>
                <select className={inputCls} value={data.contact_method}
                  onChange={(e) => set("contact_method", e.target.value)} data-testid="select-contact">
                  <option>Phone call</option>
                  <option>Text message</option>
                  <option>Email</option>
                </select>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button type="button" onClick={back} data-testid="btn-back"
                className="flex items-center gap-2 px-6 py-4 border border-ink/15 text-ink-soft font-mono text-[11px] tracking-[0.15em] uppercase hover:border-gold hover:text-gold transition-colors rounded-sm">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {step < 4 ? (
              <button type="button" onClick={next} data-testid="btn-next"
                className="flex-1 flex items-center justify-center gap-2 bg-evergreen text-cream font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-4 hover:bg-gold transition-colors rounded-sm">
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} data-testid="btn-submit"
                className="flex-1 flex items-center justify-center gap-2 bg-gold text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-4 hover:bg-gold-hover transition-colors disabled:opacity-60 rounded-sm">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                {submitting ? "Submitting" : "Submit My Information"}
              </button>
            )}
          </div>

          <p className="text-ink-soft/70 text-[11px] leading-relaxed mt-6 text-center">
            By submitting, you agree you may be contacted regarding your property and selling options.
            Consent is not required as a condition of purchasing goods or services.
          </p>
        </>
      )}
    </div>
  );
};
